import React, { useEffect, useState } from 'react';
import { OverlayView, Polyline, useGoogleMap } from '@react-google-maps/api';
import mapboxPolyline from '@mapbox/polyline';

import { Box, IconButton, LinearProgress, ProfileAvatar, Stack, Typography } from '../../../ui/core';
import { CloseIcon } from '../../../ui/icons';
import { floatSurfaceSx } from '../../../ui/styles';
import { get } from '../../../helpers/api';
import { ago } from '../../../pages/Rides/RideLiveMap';
import { fitPadding } from '../../shared/MapFirstLayout';

// Cards fetched this session, by driver id: a dot clicked twice, or the same
// driver on the board and then a trip, is one request.
const cards = new Map();

/**
 * Who a live driver is — `/admin/live/drivers/:driverId` — fetched when their
 * dot is clicked (the board's events carry only the id). `status` is
 * 'loading' | 'ready' | 'error'.
 */
export const useDriverCard = (driverId) => {
  const [state, setState] = useState(() =>
    cards.has(driverId) ? { status: 'ready', card: cards.get(driverId) } : { status: 'loading', card: null },
  );

  useEffect(() => {
    if (!driverId) return undefined;
    if (cards.has(driverId)) {
      setState({ status: 'ready', card: cards.get(driverId) });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading', card: null });
    get(`/admin/live/drivers/${encodeURIComponent(driverId)}`)
      .then(({ data }) => {
        cards.set(driverId, data);
        if (!cancelled) setState({ status: 'ready', card: data });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', card: null });
      });
    return () => {
      cancelled = true;
    };
  }, [driverId]);

  return state;
};

// Drives already routed this session, by driver and pick-up: reopening a
// card doesn't route again.
const drives = new Map();

/** "25 min" / "1 h 5 min" from OSRM's seconds. */
const durationText = (seconds) => {
  const minutes = Math.max(1, Math.round(seconds / 60));
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
};

/**
 * The drive from a driver to a pick-up by road — our OSRM, via
 * `/admin/live/drive` — as `{ path, distance, duration }` (texts), or null
 * until it's in or if there's no route. OSRM has no traffic, so the time is
 * a floor. Routed once per driver and pick-up, from where the driver was
 * when their card opened.
 */
// ponytail: not re-routed as the driver moves; re-open the card for a fresh one.
export const useDriveTo = (driver, target) => {
  const key = driver && target ? `${driver.driverId}:${target.lat},${target.lng}` : null;
  const [drive, setDrive] = useState(() => (key && drives.get(key)) || null);

  useEffect(() => {
    if (!key) return setDrive(null);
    if (drives.has(key)) return setDrive(drives.get(key));
    setDrive(null);
    let cancelled = false;
    const query = new URLSearchParams({
      fromLat: driver.lat,
      fromLng: driver.lon,
      toLat: target.lat,
      toLng: target.lng,
    });
    get(`/admin/live/drive?${query}`)
      .then(({ data }) => {
        const next = {
          path: mapboxPolyline.decode(data.polyline, 5).map(([lat, lng]) => ({ lat, lng })),
          distance: `${(data.distanceMeters / 1609.34).toFixed(1)} mi`,
          duration: durationText(data.durationSeconds),
        };
        drives.set(key, next);
        if (!cancelled) setDrive(next);
      })
      // No route, or routing is down: the card keeps the straight-line distance.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // The driver's position is read once, when the card opens: see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return drive;
};

/**
 * Which way a stretch of route runs on screen, roughly: x east, y down.
 * Good enough to pick a side at city scale.
 */
const screenDirection = (from, to) => ({
  x: (to.lng - from.lng) * Math.cos((from.lat * Math.PI) / 180),
  y: from.lat - to.lat,
});

/**
 * Whether the card should sit below the driver's dot: when the drive leaves
 * upwards, so the card doesn't cover the first stretch of it.
 */
const cardBelow = (drive) => {
  if (!drive) return false;
  const ahead = drive.path[Math.min(drive.path.length - 1, Math.ceil(drive.path.length / 4))];
  return screenDirection(drive.path[0], ahead).y < 0;
};

/**
 * Where a label goes so it sits beside a line, never on it: off to the side
 * along the line's normal (the upper one, or the right for a vertical line),
 * with its nearest corner or edge a few px from the point.
 */
const besideLine = (along, gap = 8) => {
  const length = Math.hypot(along.x, along.y) || 1;
  let n = { x: along.y / length, y: -along.x / length };
  if (n.y > 0 || (n.y === 0 && n.x < 0)) n = { x: -n.x, y: -n.y };
  // Past ~22° off an axis, lean that way; otherwise centre on it.
  const axis = (v) => (v > 0.38 ? `${gap}px` : v < -0.38 ? `calc(-100% - ${gap}px)` : '-50%');
  return `translate(${axis(n.x)}, ${axis(n.y)})`;
};

/**
 * The drive to the pick-up on the map: the route from the driver, and a small
 * label beside it (see besideLine), halfway along, with how long and how far. The map frames
 * the drive when it arrives, leaving room for the card on its side.
 */
const DriveToPickup = ({ drive, sheetPeek }) => {
  const map = useGoogleMap();
  const midIndex = Math.floor(drive.path.length / 2);
  const mid = drive.path[midIndex];
  const along = screenDirection(
    drive.path[Math.max(0, midIndex - 2)],
    drive.path[Math.min(drive.path.length - 1, midIndex + 2)],
  );
  const below = cardBelow(drive);

  useEffect(() => {
    if (!map || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    drive.path.forEach((p) => bounds.extend(p));
    // Clear of the panel or sheet, plus room for the card on its side of the dot.
    const padding = fitPadding(map, sheetPeek);
    map.fitBounds(
      bounds,
      below ? { ...padding, bottom: padding.bottom + 120 } : { ...padding, top: padding.top + 120 },
    );
  }, [map, drive, sheetPeek, below]);

  return (
    <>
      <Polyline
        path={drive.path}
        options={{ strokeColor: '#1E6FD9', strokeOpacity: 0.85, strokeWeight: 4, clickable: false, zIndex: 2 }}
      />
      {/* Under the card's pane, so the card always wins an overlap. */}
      <OverlayView position={mid} mapPaneName={OverlayView.OVERLAY_LAYER}>
        <div style={{ width: 'max-content', transform: besideLine(along), pointerEvents: 'none' }}>
          <Box
            sx={{
              px: '8px',
              py: '3px',
              borderRadius: '999px',
              backgroundColor: '#1E6FD9',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600,
              boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
              whiteSpace: 'nowrap',
            }}
          >
            {drive.duration} · {drive.distance}
          </Box>
        </div>
      </OverlayView>
    </>
  );
};

/**
 * A driver between trips: a grey dot — a background fix has no heading, so
 * there's no car to turn — and a button, opening their card. Ringed while
 * its card is open.
 */
export const OffTripDriverDot = ({ driver, selected, onSelect }) => (
  // The mouse-target pane, so the dot can take clicks.
  <OverlayView position={{ lat: driver.lat, lng: driver.lon }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
    <div
      role="button"
      tabIndex={0}
      title="Show this driver"
      onClick={() => onSelect(driver.driverId)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(driver.driverId);
      }}
      // `max-content`: the overlay's container is 0px wide; see LiveDriverMarker.
      style={{ width: 'max-content', transform: 'translate(-50%, -50%)', cursor: 'pointer' }}
    >
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          background: '#9e9e9e',
          border: '2px solid #fff',
          boxShadow: selected ? '0 0 0 3px #1E6FD9, 0 1px 3px rgba(0,0,0,0.35)' : '0 1px 3px rgba(0,0,0,0.35)',
        }}
      />
    </div>
  </OverlayView>
);

/**
 * A clicked driver's card, on the map just above their dot: face, name
 * (linking to their profile) and how long since they last reported in. On a
 * trip that needs a driver, their drive to its first pick-up is drawn too.
 */
export const LiveDriverCard = ({ driver, target, sheetPeek, onClose }) => {
  const { status, card } = useDriverCard(driver.driverId);
  const name = card ? `${card.firstName || ''} ${card.lastName || ''}`.trim() || 'Driver' : 'Driver';
  const drive = useDriveTo(driver, target);

  return (
    <>
      {drive && <DriveToPickup drive={drive} sheetPeek={sheetPeek} />}
      <OverlayView position={{ lat: driver.lat, lng: driver.lon }} mapPaneName={OverlayView.FLOAT_PANE}>
        {/* Centred on the dot, just above it — or just below when the drive
            leaves upwards, so the card doesn't cover the route. */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            ...(cardBelow(drive) ? { top: 14 } : { bottom: 14 }),
            transform: 'translateX(-50%)',
          }}
        >
          <Box
            sx={{
              ...floatSurfaceSx,
              width: 260,
              backgroundColor: 'background.paper',
              padding: '12px',
            }}
            // Clicks inside the card stay in the card, not on the map.
            onClick={(e) => e.stopPropagation()}
          >
            <Stack direction="row" spacing="10px" alignItems="flex-start">
              <ProfileAvatar name={name} photoUrl={card?.imageUrl} colorKey={name} size={40} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {card ? (
                  // The name is the way to their full profile.
                  <Typography
                    variant="subtitle2"
                    noWrap
                    component="a"
                    href={`/users/${card.userId}`}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ display: 'block', color: 'accent.main' }}
                  >
                    {name}
                  </Typography>
                ) : (
                  <Typography variant="subtitle2" noWrap>
                    {status === 'loading' ? 'Loading…' : name}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Last seen {ago(driver.timestamp)}
                </Typography>
              </Box>
              <IconButton size="small" onClick={onClose} aria-label="Close driver card" sx={{ mt: '-4px', mr: '-4px' }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
            {status === 'loading' && <LinearProgress sx={{ mt: '10px' }} />}
            {status === 'error' && (
              <Typography variant="body2" color="error.main" sx={{ mt: '8px' }}>
                Couldn't load this driver.
              </Typography>
            )}
          </Box>
        </div>
      </OverlayView>
    </>
  );
};
